import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '../atoms/Input';
import { PATHS } from '../commons/paths';
import { useAuth } from '../hooks/useAuth';
import { VisibilityButton } from '../atoms/VisibilityButton';
import { StyledFromContainer } from 'templates/StyledFormContainer';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { FormSectionTitle } from 'atoms/FormSectionTitle';
import { useCreateChangePasswordSchema } from 'hooks/u007/useCreateChangePasswordSchema';
import { CreateChangePasswordFormType } from 'commons/passwordChange';
import { FieldErrorMessages } from 'molecules/FieldErrorMessages';
import { TransitionButton } from 'atoms/TransitionButton';
import { Button } from '../atoms/Button';
import MessageModal from 'organisms/MessageModal';
import { RequiredLabel } from 'molecules/RequiredLabel';
import { AppContext } from 'templates/AppContextProvider';

export function ChangePassword() {
  // hooks
  const navigate = useNavigate();
  const auth = useAuth();
  const { t: tCommons } = useTranslationWithLang('commons');
  const ChangePasswordSchema = useCreateChangePasswordSchema();
  const { currentLang } = useContext(AppContext);
  // state
  const [isShowOldPassword, setIsShowOldPassword] = useState(false);
  const [isShowNewPassword, setIsShowNewPassword] = useState(false);
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false);
  const [infoModal, setInfoModal] = useState('');
  const [errorModal, setErrorModal] = useState('');
  // form
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm<CreateChangePasswordFormType>({
    resolver: zodResolver(ChangePasswordSchema),
    shouldFocusError: true,
  });

  useEffect(() => {
    clearErrors();
  }, [currentLang, clearErrors]);

  // function
  const onClickOldPasswordVisibilityIcon = useCallback(
    () => setIsShowOldPassword((isShowOldPassword) => !isShowOldPassword),
    [isShowOldPassword]
  );
  const onClickNewPasswordVisibilityIcon = useCallback(
    () => setIsShowNewPassword((isShowNewPassword) => !isShowNewPassword),
    [isShowNewPassword]
  );
  const onClickConfirmPasswordVisibilityIcon = useCallback(
    () => setIsShowConfirmPassword((isShowConfirmPassword) => !isShowConfirmPassword),
    [isShowConfirmPassword]
  );

  const onSubmit = async (data: CreateChangePasswordFormType) => {
    const result = await auth.changePassword(data.oldPassword, data.newPassword);
    if (result.success) {
      setInfoModal(tCommons('TU000_044'));
    } else {
      const errorMessages: { [key: string]: string } = {
        NotAuthorizedException: tCommons('TU000_039'),
        InvalidPasswordException: tCommons('TU000_040'),
        LimitExceededException: tCommons('TU000_041'),
        TooManyRequestsException: tCommons('TU000_038'),
        UnknownError: tCommons('TU000_045'),
      };
      setErrorModal(errorMessages[result.message] || tCommons('TU000_045'));
    }
  };

  // value
  const oldPasswordInputType = useMemo(
    () => (isShowOldPassword ? 'text' : 'password'),
    [isShowOldPassword]
  );
  const newPasswordInputType = useMemo(
    () => (isShowNewPassword ? 'text' : 'password'),
    [isShowNewPassword]
  );
  const confirmPasswordInputType = useMemo(
    () => (isShowConfirmPassword ? 'text' : 'password'),
    [isShowConfirmPassword]
  );

  return (
    <>
      {infoModal && (
        <MessageModal
          message={infoModal}
          closeLabel={tCommons('TU000_047')}
          onClose={() => navigate(PATHS.MYPAGE)}
        />
      )}
      {errorModal && (
        <MessageModal
          message={errorModal}
          closeLabel={tCommons('TU000_028')}
          onClose={() => setErrorModal('')}
        />
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <StyledFromContainer title={tCommons('TU000_003')}>
          <div role="heading" aria-level={1} aria-live="assertive" className="sr-only">
            {tCommons('TU000_003')}
          </div>
          <div className="flex flex-col w-full">
            <div className="inline-block relative">
              <div className="flex items-center gap-2">
                <FormSectionTitle text={tCommons('TU000_018')} htmlFor="oldPassword" />
                <RequiredLabel id="oldPassword" />
              </div>
              <div className="pt-2">
                <Input
                  id="oldPassword"
                  type={oldPasswordInputType}
                  className="rounded-lg"
                  {...register('oldPassword')}
                  aria-label={tCommons('TU000_018')}
                  autoComplete="current-password"
                />
                <VisibilityButton
                  className="absolute top-12 right-3 cursor-pointer"
                  isShow={isShowOldPassword}
                  onClick={onClickOldPasswordVisibilityIcon}
                />
                <FieldErrorMessages messages={[errors.oldPassword?.message]} />
              </div>
            </div>

            <div className="inline-block relative mt-6">
              <div className="flex items-center gap-2">
                <FormSectionTitle text={tCommons('TU000_019')} htmlFor="newPassword" />
                <RequiredLabel id="newPassword" />
              </div>
              <div className="pt-2">
                <Input
                  id="newPassword"
                  type={newPasswordInputType}
                  className="pr-10 rounded-lg"
                  {...register('newPassword')}
                  aria-label={tCommons('TU000_019')}
                  autoComplete="new-password"
                />
                <VisibilityButton
                  className="absolute top-12 right-3 cursor-pointer"
                  isShow={isShowNewPassword}
                  onClick={onClickNewPasswordVisibilityIcon}
                />
                <p className="text-sm text-[--c-text2] mb-2">{tCommons('TU000_080')}</p>
                <FieldErrorMessages messages={[errors.newPassword?.message]} />
              </div>
            </div>

            <div className="inline-block relative mt-6">
              <div className="flex items-center gap-2">
                <FormSectionTitle text={tCommons('TU000_020')} htmlFor="passwordConfirmation" />
                <RequiredLabel id="passwordConfirmation" />
              </div>
              <div className="pt-2">
                <Input
                  id="passwordConfirmation"
                  type={confirmPasswordInputType}
                  className="pr-10 rounded-lg"
                  {...register('passwordConfirmation')}
                  aria-label={tCommons('TU000_020')}
                  autoComplete="new-password"
                />
                <VisibilityButton
                  className="absolute top-12 right-3 cursor-pointer"
                  isShow={isShowConfirmPassword}
                  onClick={onClickConfirmPasswordVisibilityIcon}
                />
                <FieldErrorMessages messages={[errors.passwordConfirmation?.message]} />
              </div>
            </div>
          </div>
          <div className="flex justify-center items-center h-10 mb-10 mt-5">
            <TransitionButton className="mr-6 w-[9rem]" />
            <Button label={tCommons('TU000_048')} type="submit" className="w-[9rem]" />
          </div>
        </StyledFromContainer>
      </form>
    </>
  );
}
